/**

  ___                       _      ___       _        
 |   \ _  _ _ _  __ _ _ __ (_)__  |   \ __ _| |_ __ _ 
 | |) | || | ' \/ _` | '  \| / _| | |) / _` |  _/ _` |
 |___/ \_, |_||_\__,_|_|_|_|_\__| |___/\__,_|\__\__,_|
       |__/
       
*/

import { AssetService, AssetTypeDTO } from '../service/asset';
import { VisionWindowManager } from './manager';
import { VisionWindow } from './window';

export class VisionOrganizationWindow extends VisionWindow {
    private layout!: dhtmlXLayoutObject;
    private form!: dhtmlXForm;
    public sideBar!: dhtmlXSideBar;

    public assetTypes!: AssetTypeConfiguration;

    constructor(manager: VisionWindowManager) {
        super(manager, {
            id: 'edit_organization_window',
            title: 'Organization',
            x: 300,
            y: 300,
            width: 1600,
            height: 600,
        });
        this.window.setText('Organization Configuration');
        this.modal = true;

        this.buildLayout();
        this.assetTypes = new AssetTypeConfiguration(this);
    }

    public show() {
        super.show();
        this.assetTypes.load();
    }

    private buildLayout() {
        this.layout = this.window.attachLayout('1C');
        this.layout.cells('a').hideHeader();

        this.sideBar = this.layout.cells('a').attachSidebar();
        this.sideBar.addItem({ id: 'id1', text: 'Default Configuration' });
        this.sideBar.addItem({ id: 'asset_type', text: 'Asset Types' });

        this.form = this.sideBar.cells('id1').attachForm({});
        const form_data = [
            {
                type: 'settings',
                position: 'label-left',
                labelWidth: 90,
                inputWidth: 130,
            },
            {
                name: 'timezone',
                type: 'input',
                label: 'Default Timezone',
                value: '',
                inputWidth: '400',
                offsetLeft: '25',
                offsetTop: '25',
                disabled: false,
            },
            {
                name: 'measurement',
                type: 'select',
                label: 'Default Measurement',
                value: '',
                offsetLeft: '25',
                required: true,
                options: [
                    { value: 'imperial', text: 'Imperial' },
                    { value: 'metric', text: 'Metric' },
                ],
            },
        ];
        this.form.loadStruct(form_data);
        this.layout.cells('a').hideHeader();
    }
}

class AssetTypeConfiguration {
    private parent: VisionOrganizationWindow;
    private layout!: dhtmlXLayoutObject;
    private assetListGrid: dhtmlXGridObject;
    private assetInputGrid: dhtmlXGridObject;
    private service: AssetService;
    private assetTypes: Map<string, AssetTypeDTO>;

    constructor(parent: VisionOrganizationWindow) {
        this.service = parent.manager.app.assetService;
        this.parent = parent;
        this.layout = this.parent.sideBar.cells('asset_type').attachLayout('2U');

        this.layout.cells('a').setText('Asset Types');
        this.layout.cells('a').hideHeader();
        this.assetListGrid = this.layout.cells('a').attachGrid();
        this.assetListGrid.setHeader('Name,Assets', '', []);
        this.assetListGrid.setColSorting('str,num');
        this.assetListGrid.init();

        this.assetListGrid.attachEvent('onEditCell', (stage, rId, cInd, nValue, oValue) => {
            return this.onCellEdited(stage, rId, cInd, nValue, oValue);
        });
        this.assetListGrid.attachEvent('onRowSelect', (id, ind) => {
            this.onListSelect(id, ind);
        });

        this.assetTypes = new Map<string, AssetTypeDTO>();

        const toolbar = this.layout.cells('a').attachToolbar();
        toolbar.addButton('new', 0, 'New', 'css/imgs/new.gif', '');
        toolbar.addButton('delete', 1, 'Delete', 'css/imgs/save.gif', '');
        toolbar.attachEvent('onClick', (itemId) => {
            this.toolbarClick(itemId);
        });

        this.layout.cells('b').hideHeader();
        this.assetInputGrid = this.layout.cells('b').attachGrid();

        this.assetInputGrid.setHeader('Enabled,Number,Label,Active,Inactive,Location,Colour', '', []);
        this.assetInputGrid.setColTypes('ch,ed,ed,ed,ed,coro,cp');
        const combobox = this.assetInputGrid.getCombo(5);
        combobox.put('TOP', 'TOP');
        combobox.put('BOTTOM', 'BOTTOM');
        combobox.put('FRONT', 'FRONT');
        combobox.put('BACK', 'BACK');
        combobox.put('NONE', 'NONE');
        this.assetInputGrid.init();

        this.assetInputGrid.attachEvent('onEditCell', (stage, rId, cInd, nValue, oValue) => {
            return this.onInputCellEdited(stage, rId, cInd, nValue, oValue);
        });

        this.assetInputGrid.attachEvent('onCheck', (rId, cInd, state) => {
            this.onInputChecked(rId, cInd, state);
        });
    }

    load(): void {
        this.assetTypes.clear();

        const list = this.service.getAssetTypeList();
        list.then((data) => {
            if (data.parsedBody) {
                for (let i = 0; i < data.parsedBody?.length; i++) {
                    const item = data.parsedBody[i];
                    item.inputs.sort((a, b) => (a.inputNumber < b.inputNumber ? -1 : 1));
                    this.addToListTable(item);
                    this.assetTypes.set(item.id, item);
                }
            }
        });
    }

    toolbarClick(itemId: string) {
        console.log('Clicked asdf: ' + itemId);
        switch (itemId) {
            case 'new': {
                const newAssetType = this.service.createNewAssetType();
                this.assetInputGrid.clearAll(false);

                newAssetType.then((data) => {
                    if (data.parsedBody) {
                        this.assetTypes.set(data.parsedBody.id, data.parsedBody);
                        this.addToListTable(data.parsedBody);
                        this.displayInputTable(data.parsedBody);
                    }
                });
                break;
            }

            case 'delete': {
                const deleteRow = this.assetListGrid.getSelectedRowId();
                if (deleteRow == null) {
                    dhtmlx.alert({
                        title: 'Not Found',
                        text: 'Please select asset to delete.',
                        type: 'alert-error',
                    });
                } else {
                    this.service.deleteAssetType(deleteRow).then(() => {
                        this.assetListGrid.deleteRow(deleteRow);
                        this.assetInputGrid.clearAll(false);
                    });
                }
            }
        }
    }

    private addToListTable(type: AssetTypeDTO) {
        const data = [];
        data.push(type.name);
        this.assetListGrid.addRow(type.id, data.join(','), 0, 0, '', false);
    }

    private displayInputTable(type: AssetTypeDTO) {
        this.assetInputGrid.clearAll(false);
        for (const input of type.inputs) {
            const inputInfo = [];
            inputInfo.push(input.enabled);
            inputInfo.push(input.inputNumber);
            inputInfo.push(input.label);
            inputInfo.push(input.activeLabel);
            inputInfo.push(input.inactiveLabel);
            inputInfo.push(input.location);
            inputInfo.push(input.colour);

            this.assetInputGrid.addRow(input.id, inputInfo.join(','), 0, '', '', false);
            this.assetInputGrid.setUserData(input.id, 'owner', type.id);
        }

        console.log('Sorting');
        this.assetInputGrid.sortRows(1, 'int', 'asc');
    }

    private onCellEdited(stage: number, rId: string | number, cInd: number, nValue: string, oValue: string) {
        console.log('Stage: ' + stage);
        console.log('rId: ' + rId);
        console.log('cInd: ' + cInd);
        console.log('nValue: ' + nValue);
        console.log('oValue: ' + oValue);
        console.log(this);

        if (stage == 2 && oValue != nValue) {
            const dto = this.assetTypes.get(rId as string);
            if (dto) {
                if (cInd == 0) {
                    dto.name = nValue;
                    this.save(dto);
                }
            }
        }
        return true;
    }

    private onListSelect(id: string | number) {
        const dto = this.assetTypes.get(id as string);
        if (dto) {
            this.displayInputTable(dto);
        }
    }

    private onInputChecked(rId: string | number, cInd: number, state: boolean) {
        console.log('Input Checked Here');
        const dto = this.assetTypes.get(this.assetInputGrid.getUserData(rId, 'owner') as string);
        if (dto) {
            const inputNumber = Number(this.assetInputGrid.cells(rId, 1).getValue());
            dto.inputs[inputNumber - 1].enabled = state;
            this.save(dto);
        }
    }

    private onInputCellEdited(stage: number, rId: string | number, cInd: number, nValue: string, oValue: string) {
        console.log('onInputCellEdited===>');
        console.log('Stage: ' + stage);
        console.log('rId: ' + rId);
        console.log('cInd: ' + cInd);
        console.log('nValue: ' + nValue);
        console.log('oValue: ' + oValue);
        console.log(this);

        if (stage == 2 && oValue != nValue) {
            const dto = this.assetTypes.get(this.assetInputGrid.getUserData(rId, 'owner') as string);
            const inputNumber = Number(this.assetInputGrid.cells(rId, 1).getValue());

            if (dto) {
                switch (cInd) {
                    case 2:
                        dto.inputs[inputNumber - 1].label = nValue;
                        break;
                    case 3:
                        dto.inputs[inputNumber - 1].activeLabel = nValue;
                        break;
                    case 4:
                        dto.inputs[inputNumber - 1].inactiveLabel = nValue;
                        break;
                    case 5:
                        dto.inputs[inputNumber - 1].location = nValue;
                        break;
                    case 6:
                        dto.inputs[inputNumber - 1].colour = nValue;
                        break;
                }
                this.save(dto);
            }
        }
        return true;
    }

    private save(assetType: AssetTypeDTO) {
        this.service.saveAssetType(assetType);
    }
}
